/**
 * [ui1006, paczka KIT] ADRESY OBSZARÓW — czyste funkcje nad rejestrami czterech stron (`config/obszary`).
 *
 * Polecenie właściciela z 5.10.2026: każde miejsce strony ma mieć własny adres („odświeżenie i link wracają w to samo
 * miejsce”), a stare zakładki, kotwice i linki z maili „nie mogą pęknąć”. Ten plik jest JEDYNYM miejscem, które:
 *   • buduje adres obszaru z rejestru (`adresObszaru`, `adresWRejestrze`),
 *   • rozpoznaje obszar z adresu, w obu językach tras (`dopasujObszar`, `stronaZAdresu`),
 *   • tłumaczy stary adres albo stary stan nawigacji na nowy adres (`celStaregoAdresu`),
 *   • zna znaczenie parametrów zapytania (`znaczenieParametru`, `otwartyObiekt`, `zapytaniePoZamknieciuObiektu`,
 *     `otwartyKreator`, `zapytaniePoZamknieciuKreatora`),
 *   • wie, które wejście z panelu prowadzi do drzewa poza panelem (`adresPozaPanelem` — Wykaz KSC).
 *
 * REGUŁA PARAMETRÓW (2.3 projektu, zapadka N-18) — jedna nazwa, jedno znaczenie na stronie:
 *   `?spolka=`  ZAWSZE filtr (tak stoi w linkach powiadomień zapisanych w bazie) — nigdy obiekt;
 *   `?karta=`   spółka jako obiekt;            `&czesc=`  część otwartego obiektu;
 *   `&pokaz=`   żeton wewnątrz części;         `&krok=`   krok kreatora albo karty z krokami;
 *   `?kreator=` otwarty kreator;               `?pojecia=` otwarty słowniczek;
 *   `?widok=`   żeton listy;                   `?strona=` strona listy.
 * Zamknięcie obiektu zdejmuje parametr obiektu oraz `czesc`, `pokaz`, `krok` — i ZOSTAWIA filtry.
 * Gdy w adresie stoi `?kreator=`, `&krok=` należy do kreatora: zamknięcie obiektu go nie rusza, zamknięcie kreatora
 * zdejmuje `kreator` i `krok`, a obiekt zostaje.
 *
 * Plik nie zna Reacta. Hak `useObszar` (obok) jest cienką nakładką na te funkcje.
 */
import { getRouteLang } from "../../../utils/language";
import {
    REJESTRY, PARAMETRY, OBSZAR_STARTU,
    rejestrStrony, obszarRejestru, grupaRejestru, pierwszyObszarGrupy, obszarStartowy,
} from "../../../config/obszary";

// ── Zapytanie ──────────────────────────────────────────────────────────────────────────────────

/**
 * Zapytanie w dowolnej postaci → `URLSearchParams` (zawsze NOWY obiekt).
 * Przyjmuje: obiekt `{ nazwa: wartość }` (puste, `null` i `undefined` są pomijane), napis (`"?a=1"` albo `"a=1"`),
 * gotowe `URLSearchParams` albo nic.
 */
export const naParametry = (zapytanie) => {
    if (!zapytanie) return new URLSearchParams();
    if (zapytanie instanceof URLSearchParams) return new URLSearchParams(zapytanie);
    if (typeof zapytanie === "string") return new URLSearchParams(zapytanie);
    const parametry = new URLSearchParams();
    Object.entries(zapytanie).forEach(([nazwa, wartosc]) => {
        if (wartosc === null || wartosc === undefined || wartosc === "") return;
        parametry.set(nazwa, String(wartosc));
    });
    return parametry;
};

/** Zapytanie jako końcówka adresu: `"?a=1&b=2"` albo pusty napis. */
export const naZapytanie = (zapytanie) => {
    const tekst = naParametry(zapytanie).toString();
    return tekst ? `?${tekst}` : "";
};

const bezKoncowegoUkosnika = (sciezka) => String(sciezka ?? "").replace(/\/+$/, "");
const jezykTras = (jezyk) => (jezyk === "en" || jezyk === "pl" ? jezyk : getRouteLang());

/**
 * Dekodowanie segmentu adresu BEZ wyjątku: zniekształcone kodowanie procentowe (`%zz`, samotny `%` — adres wpisany
 * z ręki albo ucięty w komunikatorze) zostaje surowe.
 * [poprawka po przeglądzie 2026-10-06] BYŁO: `decodeURIComponent` rzucał `URIError`, więc adres `/admin/100%/x` kładł
 * nagłówek aplikacji (liczenie „Powrotu”), a `/wykaz-ksc/spolka/%zz` — hak miejsca i osłonę trasy na telefonie.
 */
export const bezpiecznieDekoduj = (segment) => {
    const surowy = String(segment ?? "");
    try { return decodeURIComponent(surowy); } catch { return surowy; }
};

/** Rejestr ze slugu strony albo z gotowego obiektu rejestru (strona wzorcowa ma własny rejestr spoza czterech stron). */
export const naRejestr = (stronaLubRejestr) => {
    if (!stronaLubRejestr) return null;
    return typeof stronaLubRejestr === "string" ? rejestrStrony(stronaLubRejestr) : stronaLubRejestr;
};

// ── Budowanie adresu ───────────────────────────────────────────────────────────────────────────

/** Adres bazowy strony w języku tras (trzy strony panelu mają ten sam w obu językach; Wykaz — `/wykaz-ksc` | `/ksc-registry`). */
export const bazaStrony = (rejestr, jezyk) => (rejestr ? rejestr.baza[jezykTras(jezyk)] || rejestr.baza.pl : null);

/** Segmenty obszaru pod adresem bazowym (`"sprawy"`, `"ustawienia/tryb"`); start — pusty napis. */
export const segmentyObszaru = (rejestr, obszar, jezyk) => {
    if (!rejestr || !obszar || !obszar.segment) return "";
    const j = jezykTras(jezyk);
    const grupa = grupaRejestru(rejestr, obszar.grupa);
    const przedrostek = grupa && grupa.segment ? `${grupa.segment[j]}/` : "";
    return `${przedrostek}${obszar.segment[j]}`;
};

/**
 * Adres obszaru z REJESTRU — bez pytania, czy strona jest już przełączona (`aktywna`). Dla powłoki, haka `useObszar`
 * i strony wzorcowej (ta rysuje ramy prawdziwych rejestrów pod własną bazą — opcja `baza`).
 * @returns {string|null} `null`, gdy obszaru nie ma albo jest dopiero zapowiedziany (`wkrotce`) — wtedy nie rysuje się odnośnika.
 */
export const adresWRejestrze = (rejestr, id, zapytanie, { jezyk, baza } = {}) => {
    const obszar = obszarRejestru(rejestr, id);
    if (!obszar || obszar.wkrotce) return null;
    const poczatek = bezKoncowegoUkosnika(baza || bazaStrony(rejestr, jezyk));
    const segmenty = segmentyObszaru(rejestr, obszar, jezyk);
    return `${poczatek}${segmenty ? `/${segmenty}` : ""}${naZapytanie(zapytanie)}`;
};

/**
 * ADRES OBSZARU DLA RESZTY APLIKACJI: `adresObszaru("office365", "analityka")`, `adresObszaru("integrations", "systemy",
 * { system: "wykaz-ksc" })`.
 * @returns {string|null} `null`, gdy rejestr strony jest jeszcze nieaktywny, obszaru nie ma albo jest zapowiedziany —
 *   wołający NIE rysuje wtedy odnośnika (albo zostaje na dotychczasowym adresie bazowym).
 */
export const adresObszaru = (strona, id, zapytanie, jezyk) => {
    const rejestr = rejestrStrony(strona);
    if (!rejestr || !rejestr.aktywna) return null;
    return adresWRejestrze(rejestr, id, zapytanie, { jezyk });
};

/**
 * Adres obiektu, który ma identyfikator W ŚCIEŻCE (wyjątek zastany: karta spółki Wykazu `/wykaz-ksc/spolka/:id`).
 * `wartosci` — parametry ścieżki (`{ id: 3 }`).
 */
export const adresObiektuTrasy = (rejestr, kluczTrasy, wartosci = {}, zapytanie, { jezyk, baza } = {}) => {
    const definicja = rejestr && (rejestr.trasyObiektow || []).find((t) => t.kluczTrasy === kluczTrasy);
    if (!definicja) return null;
    const j = jezykTras(jezyk);
    let segment = definicja.segment[j];
    Object.entries(wartosci).forEach(([nazwa, wartosc]) => { segment = segment.replace(`:${nazwa}`, encodeURIComponent(wartosc)); });
    if (segment.includes(":")) return null;
    return `${bezKoncowegoUkosnika(baza || bazaStrony(rejestr, j))}/${segment}${naZapytanie(zapytanie)}`;
};

// ── Rozpoznawanie adresu ───────────────────────────────────────────────────────────────────────

/** Wszystkie adresy, pod którymi strona się zaczyna: baza PL i EN, wejście z panelu, `/admin/<slug>`. */
const bazyStrony = (rejestr, dodatkowa) => {
    const bazy = [rejestr.baza.pl, rejestr.baza.en, rejestr.wejsciePanelu, `/admin/${rejestr.strona}`, dodatkowa];
    return [...new Set(bazy.filter(Boolean).map(bezKoncowegoUkosnika))].sort((a, b) => b.length - a.length);
};

/**
 * Część ścieżki ZA adresem bazowym strony (`"/admin/office365/ustawienia/zgody"` → `"ustawienia/zgody"`; adres bazowy →
 * pusty napis). Adres spoza strony → `null`. Granica segmentu: `/admin/office365x` nie należy do `/admin/office365`.
 */
export const resztaAdresu = (rejestr, pathname, { baza } = {}) => {
    if (!rejestr) return null;
    const sciezka = bezKoncowegoUkosnika(pathname);
    for (const poczatek of bazyStrony(rejestr, baza)) {
        if (sciezka === poczatek) return "";
        if (sciezka.startsWith(`${poczatek}/`)) return sciezka.slice(poczatek.length + 1);
    }
    return null;
};

/** Strona (rejestr) i reszta adresu dla dowolnej ścieżki aplikacji; adres spoza czterech stron → `null`. */
export const stronaZAdresu = (pathname) => {
    let najlepsza = null;
    for (const rejestr of REJESTRY) {
        const sciezka = bezKoncowegoUkosnika(pathname);
        for (const poczatek of bazyStrony(rejestr)) {
            if (sciezka !== poczatek && !sciezka.startsWith(`${poczatek}/`)) continue;
            if (!najlepsza || poczatek.length > najlepsza.poczatek.length) {
                najlepsza = { rejestr, poczatek, reszta: sciezka === poczatek ? "" : sciezka.slice(poczatek.length + 1) };
            }
            break;
        }
    }
    return najlepsza ? { rejestr: najlepsza.rejestr, reszta: najlepsza.reszta } : null;
};

/**
 * ADRES POZA PANELEM dla wejścia z panelu strony, która ma WŁASNE drzewo adresów (dziś tylko Wykaz KSC):
 * `/admin/wykaz-ksc/<cokolwiek>` → `/wykaz-ksc/<to samo>` (przy angielskich trasach `/ksc-registry/<to samo>`).
 * Sama ścieżka — zapytanie i kotwicę dokleja wołający. Jedna reguła dla routera panelu (komputer) i osłony trasy (telefon).
 *
 * [poprawka po przeglądzie 2026-10-06] BYŁO: regułę znał tylko router panelu, a na telefonie osłona trasy stoi PRZED nim —
 * link `/admin/wykaz-ksc/sprawy?sprawa=5` kończył się ekranem „otwórz ten sam link na komputerze”, choć jego cel
 * `/wykaz-ksc/sprawy?sprawa=5` na telefonie działa.
 *
 * @returns {string|null} `null`: adres nie jest wejściem z panelu strony z własnym drzewem albo nie ma podścieżki
 *   (samo `/admin/wykaz-ksc` to start strony w panelu — zostaje w panelu)
 */
export const adresPozaPanelem = (pathname, jezyk) => {
    const sciezka = bezKoncowegoUkosnika(pathname);
    for (const rejestr of REJESTRY) {
        const wejscie = bezKoncowegoUkosnika(rejestr.wejsciePanelu);
        if (!wejscie || !sciezka.startsWith(`${wejscie}/`)) continue;
        // Strona, której drzewo JEST w panelu, nie ma dokąd wychodzić (cel byłby tym samym adresem — pętla).
        if ([rejestr.baza.pl, rejestr.baza.en].map(bezKoncowegoUkosnika).includes(wejscie)) continue;
        return `${bezKoncowegoUkosnika(bazaStrony(rejestr, jezyk))}/${sciezka.slice(wejscie.length + 1)}`;
    }
    return null;
};

const wzorzecSegmentu = (segment) => new RegExp(`^${segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/:[^/]+/g, "([^/]+)")}$`);

/**
 * OBSZAR Z RESZTY ADRESU — dopasowuje segment PL albo EN (adres w „drugim” języku też otwiera właściwe miejsce).
 *
 * MIEJSCE ZAPOWIEDZIANE (`wkrotce` w rejestrze) wpisane z adresu: nie ma odnośnika w nawigacji, ale adres jest znany —
 * wynik niesie `wkrotce: true`, a powłoka zamiast treści miejsca rysuje JEDNĄ białą kartę „To miejsce jeszcze nie działa”
 * (`ObszarWBudowie wkrotce`) z przejściem na start. [poprawka po przeglądzie 2026-10-06] BYŁO: takie miejsce otwierało
 * się jak zwykłe i powłoka nie miała po czym poznać, że nie wolno ładować jego treści.
 *
 * @returns {{
 *   obszar: object|null, grupa: object|null, start: boolean, nieznany: boolean,
 *   wkrotce: boolean,            // miejsce jest w rejestrze, ale dopiero zapowiedziane — powłoka rysuje kartę, nie treść
 *   przekierujNa: string|null,   // identyfikator obszaru, na który trzeba przejść (adres samej grupy albo nieznany segment)
 *   obiektTrasy: { definicja: object, parametry: object }|null,   // obiekt z identyfikatorem w ścieżce (karta spółki Wykazu)
 * }}
 */
export const dopasujObszar = (rejestr, reszta) => {
    const pusty = { obszar: null, grupa: null, start: false, nieznany: false, wkrotce: false, przekierujNa: null, obiektTrasy: null };
    if (!rejestr) return { ...pusty, nieznany: true };
    const szukany = bezKoncowegoUkosnika(String(reszta ?? "").replace(/^\/+/, ""));
    const zGrupa = (obszar) => ({ ...pusty, obszar, grupa: grupaRejestru(rejestr, obszar.grupa), wkrotce: !!obszar.wkrotce });

    if (!szukany) return { ...zGrupa(obszarStartowy(rejestr)), start: true };

    for (const obszar of rejestr.obszary) {
        if (!obszar.segment) continue;
        if (segmentyObszaru(rejestr, obszar, "pl") === szukany || segmentyObszaru(rejestr, obszar, "en") === szukany) return zGrupa(obszar);
    }

    for (const definicja of rejestr.trasyObiektow || []) {
        for (const jezyk of ["pl", "en"]) {
            const trafienie = szukany.match(wzorzecSegmentu(definicja.segment[jezyk]));
            if (!trafienie) continue;
            const nazwy = [...definicja.segment[jezyk].matchAll(/:([^/]+)/g)].map((m) => m[1]);
            const parametry = Object.fromEntries(nazwy.map((nazwa, i) => [nazwa, bezpiecznieDekoduj(trafienie[i + 1])]));
            return { ...zGrupa(obszarRejestru(rejestr, definicja.obszar)), obiektTrasy: { definicja, parametry } };
        }
    }

    // Adres samej grupy (`…/ustawienia`, `…/settings`) → pierwszy obszar tej grupy.
    for (const grupa of rejestr.grupy) {
        if (!grupa.segment) continue;
        if (grupa.segment.pl === szukany || grupa.segment.en === szukany) {
            const pierwszy = pierwszyObszarGrupy(rejestr, grupa.id);
            if (pierwszy) return { ...pusty, grupa, przekierujNa: pierwszy.id };
        }
    }

    return { ...pusty, nieznany: true, przekierujNa: OBSZAR_STARTU };
};

// ── Stare adresy ───────────────────────────────────────────────────────────────────────────────

/** Klucze starego stanu nawigacji zużyte przez przekierowanie — powłoka nie przenosi ich dalej. */
export const KLUCZE_STAREGO_STANU = Object.freeze(["section", "rozwin", "companyId", "krok"]);

/** Stan nawigacji po przekierowaniu ze starego wejścia: bez zużytych kluczy (`origin` i `from` zostają dla „Powrotu”). */
export const stanPoPrzekierowaniu = (state) => {
    if (!state || typeof state !== "object") return state ?? null;
    const nowy = { ...state };
    KLUCZE_STAREGO_STANU.forEach((klucz) => { delete nowy[klucz]; });
    return Object.keys(nowy).length ? nowy : null;
};

/**
 * CEL STAREGO ADRESU — czysta funkcja rejestru: `{ pathname, search, state }` → nowy adres albo `null` (adres jest aktualny).
 *
 * Powłoka woła ją przy KAŻDEJ zmianie adresu (nie tylko przy montażu) i robi `replace`: przy jednej trasie z gwiazdką
 * kliknięcie powiadomienia w dzwonku, gdy człowiek już jest na tej stronie, zmienia sam adres i strony nie montuje.
 * Zapytanie przechodzi w całości (rejestr zdejmuje tylko parametry starego układu, np. `zakladka`, `sekcja`).
 *
 * Wspólna reguła dla czterech stron: adres samej grupy (`…/ustawienia`) → pierwszy obszar grupy („Uruchomienie”).
 * Reszta reguł to tabele „stare → nowe” w rejestrach (`stareAdresy`).
 *
 * Działa także dla rejestru NIEAKTYWNEGO (pyta o to wołający): ekran linku na telefonie i strona wzorcowa czytają
 * przekierowania, zanim strona zostanie przełączona.
 *
 * CEL ZASTĘPCZY: gdy stary adres prowadzi w miejsce dopiero ZAPOWIEDZIANE (`wkrotce` — nie ma jeszcze adresu), a rejestr
 * podał `zastepczo`, celem jest miejsce zastępcze (Office 365: dawna zakładka „Zarządzanie” → „Dziennik modułu”, dopóki
 * „Reakcje” nie działają). Bez celu zastępczego adres zostaje, jak był. Po zdjęciu `wkrotce` wygrywa cel właściwy —
 * w rejestrze nic się wtedy nie zmienia.
 *
 * @param {string|object} strona slug strony albo obiekt rejestru (strona wzorcowa, testy na kopii rejestru)
 * @param {{ pathname: string, search?: string, state?: object|null }} wejscie
 * @param {{ jezyk?: "pl"|"en", baza?: string }} [opcje]
 * @returns {string|null} adres (ścieżka + zapytanie) do `navigate(cel, { replace: true })`
 */
export const celStaregoAdresu = (strona, wejscie, opcje = {}) => {
    const rejestr = naRejestr(strona);
    if (!rejestr || !wejscie) return null;
    const opis = opisStaregoAdresu(rejestr, wejscie, opcje);
    if (!opis) return null;
    const cel = adresWRejestrze(rejestr, opis.obszar, opis.zapytanie, opcje)
        || (opis.zastepczo ? adresWRejestrze(rejestr, opis.zastepczo.obszar, opis.zastepczo.zapytanie, opcje) : null);
    if (!cel) return null;
    const obecny = `${bezKoncowegoUkosnika(wejscie.pathname)}${naZapytanie(wejscie.search)}`;
    return cel === obecny ? null : cel;
};

/**
 * Opis celu starego adresu w słowach rejestru: `{ obszar, zapytanie, zastepczo? }` albo `null` (wspólne dla przekierowania
 * i ekranu telefonu). `obszar` to miejsce, o które CHODZIŁO w linku — także wtedy, gdy jest dopiero zapowiedziane;
 * `zastepczo` (`{ obszar, zapytanie }`) podaje rejestr tam, gdzie na czas zapowiedzi jest dokąd pójść.
 */
export const opisStaregoAdresu = (rejestr, wejscie, { baza } = {}) => {
    const reszta = resztaAdresu(rejestr, wejscie.pathname, { baza });
    if (reszta === null) return null;
    const parametry = naParametry(wejscie.search);
    const dopasowanie = dopasujObszar(rejestr, reszta);
    if (dopasowanie.przekierujNa && !dopasowanie.nieznany) return { obszar: dopasowanie.przekierujNa, zapytanie: parametry };
    if (typeof rejestr.stareAdresy !== "function") return null;
    const opis = rejestr.stareAdresy({
        pathname: wejscie.pathname,
        search: wejscie.search || "",
        state: wejscie.state || null,
        reszta,
        parametry,
    });
    return opis && opis.obszar ? opis : null;
};

// ── Znaczenie parametrów ───────────────────────────────────────────────────────────────────────

/** Parametry zdejmowane razem z obiektem zawsze (część, żeton części). */
const PARAMETRY_OBIEKTU = [PARAMETRY.czesc, PARAMETRY.pokaz];

/**
 * CZYJ JEST `&krok=`. Gdy w adresie stoi `?kreator=`, krok należy do KREATORA (`?kreator=<nazwa>&krok=N` — 2.3 projektu);
 * bez kreatora — do otwartego obiektu (krok karty z krokami albo kreatora osadzonego w części: `?system=teamviewer&czesc=import&krok=2`).
 * [poprawka po przeglądzie 2026-10-06] BYŁO: krok zawsze przypisany obiektowi — przy kreatorze otwartym nad obiektem
 * (alert → „Nie alarmuj więcej…”) zamknięcie obiektu kasowało krok kreatora, a sam kreator zostawał w adresie.
 */
const krokObiektu = (parametry) => (parametry.get(PARAMETRY.kreator) ? null : parametry.get(PARAMETRY.krok));

/**
 * Co znaczy parametr zapytania w tym obszarze.
 * @returns {"filtr"|"obiekt"|"obiekt-dodatkowy"|"widok"|"strona"|"czesc"|"pokaz"|"krok"|"kreator"|"pojecia"|null}
 */
export const znaczenieParametru = (obszar, nazwa, obiektTrasy = null) => {
    if (!nazwa) return null;
    if (obiektTrasy && obiektTrasy.definicja.parametrCzesci === nazwa) return "czesc";
    if (nazwa === PARAMETRY.spolka) return "filtr";
    if (nazwa === PARAMETRY.pojecia) return "pojecia";
    if (nazwa === PARAMETRY.kreator) return "kreator";
    if (nazwa === PARAMETRY.krok) return "krok";
    if (nazwa === PARAMETRY.czesc) return "czesc";
    if (nazwa === PARAMETRY.pokaz) return "pokaz";
    if (nazwa === PARAMETRY.strona) return "strona";
    if (nazwa === PARAMETRY.widok) return "widok";
    if (!obszar) return null;
    if ((obszar.obiekty || []).some((o) => o.parametr === nazwa)) return "obiekt";
    if ((obszar.obiekty || []).some((o) => (o.dodatkowe || []).includes(nazwa))) return "obiekt-dodatkowy";
    if ((obszar.filtry || []).includes(nazwa)) return "filtr";
    return null;
};

/**
 * OTWARTY OBIEKT z adresu obszaru.
 * Obiekt zagnieżdżony (`?system=sms&konto=5` — konto bramki wewnątrz systemu) wygrywa z rodzicem i niesie go w polu `rodzic`.
 * `krok` jest krokiem OBIEKTU tylko wtedy, gdy w adresie nie ma kreatora (patrz `otwartyKreator`).
 *
 * @returns {{ definicja: object, parametr: string|null, wartosc: string, czesc: string|null, pokaz: string|null,
 *            krok: string|null, rodzic: { parametr: string, wartosc: string }|null, zeSciezki: boolean }|null}
 */
export const otwartyObiekt = (obszar, search, obiektTrasy = null) => {
    const parametry = naParametry(search);
    if (obiektTrasy) {
        const { definicja, parametry: zeSciezki } = obiektTrasy;
        return {
            definicja,
            parametr: null,
            wartosc: zeSciezki[definicja.parametr],
            czesc: parametry.get(definicja.parametrCzesci || PARAMETRY.czesc) || definicja.czescDomyslna || null,
            pokaz: parametry.get(PARAMETRY.pokaz),
            krok: krokObiektu(parametry),
            rodzic: null,
            zeSciezki: true,
        };
    }
    if (!obszar) return null;
    const definicje = obszar.obiekty || [];
    const obecne = definicje.filter((d) => parametry.get(d.parametr) && (!d.wymaga || parametry.get(d.wymaga)));
    if (!obecne.length) return null;
    const definicja = obecne.find((d) => d.wymaga) || obecne[0];
    return {
        definicja,
        parametr: definicja.parametr,
        wartosc: parametry.get(definicja.parametr),
        czesc: parametry.get(PARAMETRY.czesc),
        pokaz: parametry.get(PARAMETRY.pokaz),
        krok: krokObiektu(parametry),
        rodzic: definicja.wymaga ? { parametr: definicja.wymaga, wartosc: parametry.get(definicja.wymaga) } : null,
        zeSciezki: false,
    };
};

/**
 * Zapytanie PO ZAMKNIĘCIU obiektu: znikają parametr obiektu (z jego parametrami dodatkowymi, np. `etap` karty procedury)
 * oraz `czesc`, `pokaz` i krok OBIEKTU; filtry, żeton listy i strona listy ZOSTAJĄ. Zamknięcie obiektu zagnieżdżonego
 * wraca do rodzica (konto bramki → system „Bramka SMS”): znika samo konto, system zostaje. Rodzic nie ma czego „zabierać” —
 * dopóki w adresie stoi obiekt zagnieżdżony, to ON jest obiektem otwartym, więc zamyka się warstwami, od wierzchu.
 * Kreator otwarty nad obiektem (`?alert=5&kreator=wylaczenie&krok=2`) zostaje NIETKNIĘTY razem ze swoim krokiem —
 * zamyka go `zapytaniePoZamknieciuKreatora`.
 * @returns {string} `"?spolka=2&widok=otwarte"` albo pusty napis
 */
export const zapytaniePoZamknieciuObiektu = (obszar, search, obiektTrasy = null) => {
    const parametry = naParametry(search);
    const obiekt = otwartyObiekt(obszar, search, obiektTrasy);
    if (!obiekt) return naZapytanie(parametry);
    PARAMETRY_OBIEKTU.forEach((nazwa) => parametry.delete(nazwa));
    if (!parametry.get(PARAMETRY.kreator)) parametry.delete(PARAMETRY.krok);
    if (obiekt.zeSciezki) {
        if (obiekt.definicja.parametrCzesci) parametry.delete(obiekt.definicja.parametrCzesci);
        return naZapytanie(parametry);
    }
    parametry.delete(obiekt.parametr);
    (obiekt.definicja.dodatkowe || []).forEach((nazwa) => parametry.delete(nazwa));
    return naZapytanie(parametry);
};

// ── Kreator w adresie ──────────────────────────────────────────────────────────────────────────

/**
 * OTWARTY KREATOR z adresu: `?kreator=<nazwa>&krok=N` (wartości pól nigdy nie trafiają do adresu — 2.3 projektu).
 * Definicję bierze z miejsca, a gdy miejsce jej nie ma — z całej strony (podaj `rejestr`): ten sam kreator bywa otwierany
 * z kilku miejsc (Office 365: „Dodaj wyłączenie” z alertu, z karty konta i z miejsca „Wyłączenia”). Nazwa kreatora jest
 * jednoznaczna w obrębie strony (zapadka N-18).
 *
 * Krok to liczba od 1 do liczby kroków kreatora; brak, śmieć albo liczba spoza zakresu → 1 (pierwszy krok — kreator sam
 * pilnuje, do którego kroku wolno wejść).
 *
 * @param {object|null} obszar miejsce z rejestru
 * @param {string|object|URLSearchParams} search zapytanie
 * @param {object|null} [rejestr] rejestr strony — do znalezienia kreatora zdefiniowanego w INNYM miejscu tej strony
 * @returns {{ nazwa: string, definicja: object, krok: number, zInnegoMiejsca: boolean }|null}
 *   `null`, gdy w adresie nie ma kreatora albo nazwa nie jest kreatorem tej strony (powłoka czyści wtedy adres
 *   funkcją `zapytaniePoZamknieciuKreatora`)
 */
export const otwartyKreator = (obszar, search, rejestr = null) => {
    const parametry = naParametry(search);
    const nazwa = parametry.get(PARAMETRY.kreator);
    if (!nazwa) return null;
    const wMiejscu = ((obszar && obszar.kreatory) || []).find((k) => k.nazwa === nazwa) || null;
    const naStronie = wMiejscu || (rejestr ? rejestr.obszary.flatMap((o) => o.kreatory || []).find((k) => k.nazwa === nazwa) || null : null);
    if (!naStronie) return null;
    const surowy = String(parametry.get(PARAMETRY.krok) ?? "");
    const liczba = /^\d+$/.test(surowy) ? Number(surowy) : 1;
    const krok = liczba >= 1 && liczba <= naStronie.kroki ? liczba : 1;
    return { nazwa, definicja: naStronie, krok, zInnegoMiejsca: !wMiejscu };
};

/**
 * Zapytanie PO ZAMKNIĘCIU kreatora: znikają `kreator` i jego `krok`; otwarty obiekt, jego część i żeton części, filtry,
 * żeton listy i strona listy ZOSTAJĄ (kreator otwarty nad alertem wraca do alertu). Bez kreatora w adresie zapytanie
 * wychodzi bez zmian — krok należy wtedy do obiektu i nie wolno go ruszyć.
 * @returns {string} `"?alert=5&spolka=3"` albo pusty napis
 */
export const zapytaniePoZamknieciuKreatora = (search) => {
    const parametry = naParametry(search);
    if (!parametry.has(PARAMETRY.kreator)) return naZapytanie(parametry);
    parametry.delete(PARAMETRY.kreator);
    parametry.delete(PARAMETRY.krok);
    return naZapytanie(parametry);
};

/** Filtry obszaru obecne w adresie: `{ spolka: "2", poziom: "K" }` (tylko nazwy z rejestru; `spolka` zawsze). */
export const filtryZAdresu = (obszar, search) => {
    const parametry = naParametry(search);
    const nazwy = new Set([...(obszar ? obszar.filtry || [] : []), PARAMETRY.spolka]);
    const wynik = {};
    nazwy.forEach((nazwa) => { if (parametry.get(nazwa)) wynik[nazwa] = parametry.get(nazwa); });
    return wynik;
};

// ── Opis linku (ekran telefonu, „Powrót”) ───────────────────────────────────────────────────────

/**
 * CZEGO DOTYCZY LINK do jednej z czterech stron — także stary (`?zakladka=incydenty&incydent=7`) i także wtedy, gdy strona
 * nie jest jeszcze przełączona. Czyta wyłącznie rejestr.
 * @returns {{ rejestr: object, obszar: object|null, obiekt: object|null }|null} `null` = adres spoza czterech stron
 */
export const opisLinku = ({ pathname, search = "", state = null } = {}) => {
    const trafienie = stronaZAdresu(pathname);
    if (!trafienie) return null;
    const { rejestr, reszta } = trafienie;
    const stary = opisStaregoAdresu(rejestr, { pathname, search, state });
    if (stary) {
        const obszar = obszarRejestru(rejestr, stary.obszar);
        return { rejestr, obszar, obiekt: otwartyObiekt(obszar, stary.zapytanie) };
    }
    const dopasowanie = dopasujObszar(rejestr, reszta);
    return { rejestr, obszar: dopasowanie.obszar, obiekt: otwartyObiekt(dopasowanie.obszar, search, dopasowanie.obiektTrasy) };
};
